import React from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { CarbonAvoidedByMaterial } from '../../types/esg-dashboard.type';
import { carbonMaterialLabel, formatTco2e } from '../../utils/carbonAvoidance.util';

interface CarbonAvoidedByMaterialTableProps {
  rows: CarbonAvoidedByMaterial[];
  title?: string;
  fillHeight?: boolean;
}

const CarbonAvoidedByMaterialTable: React.FC<CarbonAvoidedByMaterialTableProps> = ({
  rows,
  title,
  fillHeight = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const resolvedTitle = title ?? t('metrics.carbon_avoidance_material');
  const paperSx = {
    p: 3,
    borderRadius: '16px',
    boxShadow: theme.shadows[2],
    ...(fillHeight
      ? {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }
      : {}),
  };

  if (!rows.length) {
    return (
      <Paper sx={paperSx}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {resolvedTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.alerts.noCarbonData')}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={paperSx}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {resolvedTitle}
      </Typography>
      <TableContainer sx={fillHeight ? { flex: 1 } : undefined}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('carbonAvoidance.material')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('carbonAvoidance.stream')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                {t('carbonAvoidance.avoidedTco2e')}
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('carbonAvoidance.efVersion')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((group) => (
              <React.Fragment key={group.category_label}>
                <TableRow sx={{ bgcolor: theme.palette.action.hover }}>
                  <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                    {group.category_label}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {formatTco2e(group.total_avoided_tco2e)}
                  </TableCell>
                  <TableCell />
                </TableRow>
                {group.materials.map((line, idx) => (
                  <TableRow key={`${group.category_label}-${line.material_type}-${idx}`}>
                    <TableCell sx={{ pl: 4 }}>{line.material_type}</TableCell>
                    <TableCell>{carbonMaterialLabel(line.material_label)}</TableCell>
                    <TableCell align="right">{formatTco2e(line.avoided_tco2e)}</TableCell>
                    <TableCell>
                      <Box component="span" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                        {line.emission_factor_version_label}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CarbonAvoidedByMaterialTable;
